import { useNavigate } from '@tanstack/react-router'
import { useRolodexSignedURLs } from 'features/rolodex'
import { ReactNode } from 'react'
import { GeckoGeographicPin, RolodexEntryType } from 'types'

/**
 * A function that returns interaction-actions for geographic pins (click handler, and image to display on hover.
 * @param pin
 */
export function useGetGeographicPinInteractionHandler(pin: GeckoGeographicPin) {
  const handleClick = useGetGeographicPinHandleClick(pin)
  const hoverImage = useGetGeographicPinImage(pin)

  return {
    handleClick,
    hoverImage,
  }
}

// ----------------------------------------------------------------------------------------------------
function useGetGeographicPinImage(
  pin: GeckoGeographicPin
): ReactNode | undefined {
  const { url: thumbnailURL } = useRolodexSignedURLs({
    entry_id: pin.id,
    path: 'thumbnail.png',
  })

  if (!thumbnailURL) {
    return undefined
  }

  return <img src={thumbnailURL} alt={pin.data.name ?? pin.id} />
}
// ----------------------------------------------------------------------------------------------------
/**
 * A function that returns a click handler for geographic pins.
 */
function useGetGeographicPinHandleClick(pin: GeckoGeographicPin) {
  const navigate = useNavigate()

  const potreePointCloud = pin.tags[RolodexEntryType.POTREE_POINT_CLOUD]
  const hardcodedLink = pin.data.link

  const pointCloudStartTarget =
    pin.data.geo_json.properties?.point_cloud_start_target
  const pointCloudStartPosition =
    pin.data.geo_json.properties?.point_cloud_start_position

  const handleClick = (): React.ReactNode => {
    // If no link and no potree point cloud tag, don't handle click
    if (!hardcodedLink && !potreePointCloud) {
      return null
    }

    // Use hardcoded link if available
    if (hardcodedLink) {
      // Parse the URL to separate path and search parameters
      const [pathname, search] = hardcodedLink.split('?')

      if (search) {
        // Convert search string to URLSearchParams for proper handling
        const searchParams = new URLSearchParams(search)
        const searchObject: Record<string, string | boolean> = {}

        searchParams.forEach((value, key) => {
          // Convert boolean strings to actual booleans
          if (value === 'true') {
            searchObject[key] = true
          } else if (value === 'false') {
            searchObject[key] = false
          } else {
            searchObject[key] = value
          }
        })

        navigate({
          to: pathname,
          search: searchObject,
        })
      } else {
        navigate({
          to: pathname,
        })
      }
      return null
    }

    // Fallback to potree point cloud logic
    if (potreePointCloud) {
      const searchParams: { cameraPos?: string; cameraTarget?: string } = {}

      if (
        pointCloudStartPosition &&
        Array.isArray(pointCloudStartPosition) &&
        pointCloudStartPosition.length === 3
      ) {
        searchParams.cameraPos = pointCloudStartPosition.join(',')
      }

      if (
        pointCloudStartTarget &&
        Array.isArray(pointCloudStartTarget) &&
        pointCloudStartTarget.length === 3
      ) {
        searchParams.cameraTarget = pointCloudStartTarget.join(',')
      }

      navigate({
        to: '/point-cloud/' + pin.tags.site + '/' + potreePointCloud,
        search: searchParams,
      })
    }

    return null
  }

  return handleClick
}
